import { signInWithEmailAndPassword } from "firebase/auth";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ErrorDisplay from "../../components/ErrorDisplay/ErrorDisplay";
import { Headings } from "../../components/Headings";
import PageLayout from "../../components/PageLayout/PageLayout";
import PhoneLayout from "../../components/PhoneLayout/PhoneLayout";
import { auth as firebaseAuth } from "../../firebase";
import { useAppAuthState } from "../../hooks/useAppAuthState";
import {
  logAnonActivity,
  UserActivityEventType,
} from "../../services/activityLogger";
import { SitePage } from "../../types/SitePage";
import "./LoginPage.scss";

function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoggedIn } = useAppAuthState();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleLoginSuccess = useCallback(() => {
    const redirectUrl = searchParams.get("redirect");
    if (redirectUrl) {
      navigate(redirectUrl);
    } else {
      navigate("/map");
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    if (isLoggedIn) {
      handleLoginSuccess();
    }
  }, [isLoggedIn, handleLoginSuccess]);

  const handleLogin = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setError(null);
      setLoading(true);

      try {
        logAnonActivity({
          eventType: UserActivityEventType.USER_LOGIN,
          timestamp: Date.now(),
          context: {
            email,
          },
        });

        await signInWithEmailAndPassword(firebaseAuth, email, password);
        handleLoginSuccess();
      } catch (err: any) {
        if (err.code === "auth/invalid-credential") {
          setError("Invalid email or password.");
        } else if (err.code === "auth/user-not-found") {
          setError("User not found. Please register.");
        } else if (err.code && err.message) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred.");
        }
      } finally {
        setLoading(false);
      }
    },
    [email, password, handleLoginSuccess]
  );

  return (
    <PageLayout page={SitePage.Login} title="Login">
      <PhoneLayout>
        <Headings.PhoneMain>Login</Headings.PhoneMain>
        <form onSubmit={handleLogin} className="login-form">
          {error && <ErrorDisplay error={new Error(error)} />}
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          <button
            type="submit"
            className="login-button btn btn-primary"
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </button>
          <p className="register-link">
            Don't have an account? <Link to="/register">Register here</Link>
          </p>
        </form>
      </PhoneLayout>
    </PageLayout>
  );
}

export default LoginPage;
